import { useCallback, useEffect, useMemo, useState } from 'react'
import type { Tag, TimeEntry } from '#shared/types'
import type { Project } from '#shared/projects'
import { formatHours } from '#shared/duration'
import { addDays, startOfWeek, todayLocal, weekOf } from '#shared/dates'
import { weekGrid } from '#shared/reports'
import { api } from '../api/client'
import { WeekGrid } from './WeekGrid'
import { CellEntryDialog, type CellEntry } from './CellEntryDialog'

interface Props {
  /**
   * Bumped by the page after it logs time, so this panel refetches rather
   * than showing a week that is missing the entry just added.
   */
  refreshToken?: number
  /** Called after this panel changes something, so the page can catch up. */
  onChanged?: () => void
}

/**
 * The signed-in person's own week, as a project-by-day grid.
 *
 * Fetches its own entries, projects and tags rather than taking them from
 * the Dashboard; the two props above are only for keeping the form above it
 * and this grid in step.
 *
 * Clicking a cell opens the day and project it names, which is the whole
 * point of the grid over the tab bar it replaced: a gap in the table is a
 * thing you can click on rather than a thing you have to go and find.
 */
export function MyWeek({ refreshToken = 0, onChanged }: Props) {
  const today = useMemo(() => todayLocal(), [])
  const [monday, setMonday] = useState(() => startOfWeek(today))
  const days = useMemo(() => weekOf(monday), [monday])

  const [entries, setEntries] = useState<TimeEntry[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [tags, setTags] = useState<Tag[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  /** The open cell, or null. */
  const [cell, setCell] = useState<{ projectId: string; date: string } | null>(null)

  const load = useCallback(async () => {
    const [weekEntries, allProjects, allTags] = await Promise.all([
      api.listEntries({ from: days[0], to: days[6] }),
      // Archived rows included, so a week that references an archived project
      // or tag still shows a name rather than a raw id.
      api.listProjects(true),
      api.listTags(true),
    ])
    setEntries(weekEntries)
    setProjects(allProjects)
    setTags(allTags)
  }, [days])

  useEffect(() => {
    let cancelled = false
    setLoading(true)

    load()
      .then(() => {
        if (!cancelled) setError(null)
      })
      .catch(() => {
        if (!cancelled) setError('Could not load this week.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [load, refreshToken])

  const projectNames = useMemo(
    () => new Map(projects.map((p) => [p.id, p.name])),
    [projects],
  )
  // Position in the manager's order, so the chips on a row read in the same
  // sequence as the picker that produced them.
  const tagRank = useMemo(() => new Map(tags.map((t, index) => [t.id, index])), [tags])
  const tagNames = useMemo(() => new Map(tags.map((t) => [t.id, t.name])), [tags])

  const grid = useMemo(
    () => weekGrid(entries, projectNames, days),
    [entries, projectNames, days],
  )

  const openProject = cell ? projects.find((p) => p.id === cell.projectId) : undefined

  const cellEntries: CellEntry[] = cell
    ? entries
        .filter((e) => e.projectId === cell.projectId && e.date === cell.date)
        .map((entry) => ({
          id: entry.id,
          minutes: entry.minutes,
          notes: entry.notes ?? null,
          tags: [...entry.tagIds]
            .sort((a, b) => (tagRank.get(a) ?? Infinity) - (tagRank.get(b) ?? Infinity))
            .map((id) => tagNames.get(id) ?? id),
          // The names are what the row reads as; the ids are what the edit
          // form needs to preselect the chips.
          tagIds: entry.tagIds,
        }))
    : []

  async function refresh() {
    try {
      await load()
      setError(null)
      onChanged?.()
    } catch {
      setError('Could not reload this week.')
    }
  }

  /**
   * Rethrows after reporting, so the dialog can tell a delete that worked
   * from one that did not: it closes on success, and a dialog that closed
   * over a failed delete would look exactly like one that succeeded.
   */
  async function handleDelete(entry: CellEntry) {
    if (!cell) return
    try {
      await api.deleteEntry(cell.date, entry.id)
      await refresh()
    } catch (err) {
      setError('Could not delete that entry.')
      throw err
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h2>My week</h2>
        <span className="count">{formatHours(grid.total)} total</span>
      </div>

      {error && <p className="error">{error}</p>}

      <div className={loading ? 'refetching' : undefined}>
        <WeekGrid
          days={days}
          grid={grid}
          today={today}
          onPrev={() => setMonday(addDays(monday, -7))}
          onNext={() => setMonday(addDays(monday, 7))}
          canGoForward={monday < startOfWeek(today)}
          onCell={(projectId, date) => setCell({ projectId, date })}
        />
      </div>

      {cell && openProject && (
        <CellEntryDialog
          project={openProject}
          // Only the live ones: an entry may be moved onto a project that
          // is still in use, not onto one that has been archived.
          projects={projects.filter((p) => !p.archived)}
          date={cell.date}
          today={today}
          entries={cellEntries}
          tags={tags}
          canEdit
          onClose={() => setCell(null)}
          onChanged={refresh}
          onDelete={handleDelete}
        />
      )}
    </div>
  )
}
